import type { KaraokeNote, SongDetail } from '@pipi/shared';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CircleStop,
  Gauge,
  Heart,
  ListMusic,
  LoaderCircle,
  Mic2,
  MicOff,
  Pause,
  Play,
  RotateCcw,
  SkipForward,
  Sparkles,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProviderBadge } from '../components/provider-badge';
import { TvButton } from '../components/tv-button';
import { YouTubePlayer, type YouTubePlayerHandle } from '../components/youtube-player';
import { useMicrophone } from '../hooks/use-microphone';
import { api } from '../lib/api';
import { octaveInvariantDistance } from '../lib/pitch';
import { useTvNavigation } from '../lib/tv-navigation';
import { useAppState } from '../state/app-state';

function formatTime(ms: number) {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

function currentNoteFor(notes: KaraokeNote[], timeMs: number) {
  return notes.find((note) => timeMs >= note.startMs && timeMs < note.startMs + note.durationMs) ?? null;
}

function lineText(song: SongDetail, line: number) {
  return song.notes.filter((note) => note.line === line);
}

function scoreGrade(score: number) {
  if (score >= 9200) return { label: '闪耀舞台', letter: 'S', color: '#ffd968' };
  if (score >= 8000) return { label: '状态真好', letter: 'A', color: '#65dfbd' };
  if (score >= 6500) return { label: '越来越稳', letter: 'B', color: '#66b9ff' };
  return { label: '再唱一次', letter: 'C', color: '#ff8218' };
}

export function PlayerPage() {
  const { songId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, addToQueue, setFavorite, notify, refresh } = useAppState();
  const [song, setSong] = useState<SongDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentMs, setCurrentMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.86);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [hudVisible, setHudVisible] = useState(true);
  const [scoreSamples, setScoreSamples] = useState({ total: 0, hit: 0, perfect: 0 });
  const [finished, setFinished] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState<string | null>(null);
  const nativeRef = useRef<HTMLMediaElement>(null);
  const youtubeRef = useRef<YouTubePlayerHandle>(null);
  const hideTimer = useRef<number | null>(null);
  const completionSent = useRef(false);
  const microphone = useMicrophone();
  const queueId = (location.state as { queueId?: string } | null)?.queueId ?? data?.queue.find((item) => item.song.id === songId)?.id;
  const onBack = useCallback(() => navigate(-1), [navigate]);
  useTvNavigation(onBack);

  const revealHud = useCallback(() => {
    setHudVisible(true);
    if (hideTimer.current) window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => playing && setHudVisible(false), 4200);
  }, [playing]);

  useEffect(() => {
    const handle = () => revealHud();
    window.addEventListener('keydown', handle);
    window.addEventListener('pointermove', handle);
    return () => {
      window.removeEventListener('keydown', handle);
      window.removeEventListener('pointermove', handle);
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
    };
  }, [revealHud]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    void api.song(songId)
      .then((detail) => {
        setSong(detail);
        setDurationMs(detail.durationMs ?? 0);
      })
      .catch((requestError) => setError(requestError instanceof Error ? requestError.message : '歌曲加载失败'))
      .finally(() => setLoading(false));
  }, [songId]);

  const onEnded = useCallback(() => {
    setPlaying(false);
    setHudVisible(true);
    setFinished(true);
  }, []);

  const onYoutubeTime = useCallback((time: number, duration: number) => {
    setCurrentMs(time);
    if (duration) setDurationMs(duration);
  }, []);
  const onYoutubePlaying = useCallback((value: boolean) => setPlaying(value), []);
  const onYoutubeError = useCallback((message: string) => setError(message), []);

  const togglePlay = useCallback(() => {
    if (!song) return;
    if (song.media.kind === 'youtube') {
      if (playing) youtubeRef.current?.pause();
      else youtubeRef.current?.play();
    } else if (nativeRef.current) {
      if (playing) nativeRef.current.pause();
      else void nativeRef.current.play().catch(() => notify('电视阻止了自动播放，请再按一次 OK', 'error'));
    }
    revealHud();
  }, [notify, playing, revealHud, song]);

  useEffect(() => {
    const command = (event: Event) => {
      const value = (event as CustomEvent).detail;
      if (value === 'toggle') togglePlay();
      if (value === 'play' && !playing) togglePlay();
      if (value === 'pause' && playing) togglePlay();
      if (value === 'seek-forward') {
        if (song?.media.kind === 'youtube') youtubeRef.current?.seek(currentMs / 1000 + 10);
        else if (nativeRef.current) nativeRef.current.currentTime += 10;
      }
    };
    window.addEventListener('pipi:player-command', command);
    return () => window.removeEventListener('pipi:player-command', command);
  }, [currentMs, playing, song, togglePlay]);

  const currentNote = useMemo(() => song ? currentNoteFor(song.notes, currentMs) : null, [song, currentMs]);
  const currentLine = currentNote?.line ?? song?.notes.find((note) => note.startMs > currentMs)?.line ?? 0;
  const activeLine = useMemo(() => song ? lineText(song, currentLine) : [], [song, currentLine]);
  const nextLine = useMemo(() => song ? lineText(song, currentLine + 1) : [], [song, currentLine]);

  useEffect(() => {
    if (!playing || microphone.status !== 'active' || microphone.midi === null || currentNote?.pitch === null || currentNote?.pitch === undefined) return;
    const distance = octaveInvariantDistance(microphone.midi, currentNote.pitch);
    setScoreSamples((sample) => ({
      total: sample.total + 1,
      hit: sample.hit + (distance <= 1.5 ? 1 : 0),
      perfect: sample.perfect + (distance <= 0.55 ? 1 : 0),
    }));
  }, [currentNote, microphone.midi, microphone.status, playing]);

  const score = scoreSamples.total ? Math.round((scoreSamples.hit / scoreSamples.total) * 8200 + (scoreSamples.perfect / scoreSamples.total) * 1800) : 0;
  const accuracy = scoreSamples.total ? Math.round((scoreSamples.hit / scoreSamples.total) * 100) : 0;

  useEffect(() => {
    if (!finished || completionSent.current || !queueId) return;
    completionSent.current = true;
    void api.queueStatus(queueId, 'completed', score).then(() => refresh(true));
  }, [finished, queueId, refresh, score]);

  const updateVolume = (next: number) => {
    const value = Math.max(0, Math.min(1, next));
    setVolume(value);
    if (nativeRef.current) nativeRef.current.volume = value;
    youtubeRef.current?.setVolume(value);
  };

  const cycleSpeed = () => {
    const speeds = [0.85, 1, 1.1, 1.25];
    const next = speeds[(speeds.indexOf(speed) + 1) % speeds.length] ?? 1;
    setSpeed(next);
    if (nativeRef.current) nativeRef.current.playbackRate = next;
  };

  const toggleRecording = async () => {
    if (microphone.recording) {
      const blob = await microphone.stopRecording();
      if (blob) {
        if (recordingUrl) URL.revokeObjectURL(recordingUrl);
        setRecordingUrl(URL.createObjectURL(blob));
        notify('人声录音已准备下载', 'success');
      }
      return;
    }
    if (microphone.status !== 'active') {
      await microphone.start();
      window.setTimeout(() => microphone.startRecording(), 350);
    } else {
      microphone.startRecording();
    }
  };

  const retry = () => {
    setFinished(false);
    setScoreSamples({ total: 0, hit: 0, perfect: 0 });
    completionSent.current = false;
    setCurrentMs(0);
    if (nativeRef.current) nativeRef.current.currentTime = 0;
    youtubeRef.current?.seek(0);
  };

  const nextQueueItem = data?.queue.find((item) => item.id !== queueId);

  if (loading) return <main className="player-loading"><LoaderCircle className="spin" /><h1>正在准备麦克风和歌词</h1></main>;
  if (!song || error && !song) return <main className="player-error"><img src="/brand/pipi-karaoke-mascot.png" alt="" /><h1>这首歌暂时没有准备好</h1><p>{error}</p><TvButton tone="primary" onClick={onBack}><ArrowLeft />返回曲库</TvButton></main>;

  return (
    <main className={`player-page ${hudVisible ? 'has-hud' : ''} ${song.capabilities.video ? 'is-video' : 'is-audio'}`}>
      <div className="player-atmosphere" style={{ '--player-accent': song.accent || '#ff8218' } as React.CSSProperties} />
      <header className="player-header">
        <TvButton iconOnly tone="quiet" aria-label="返回" onClick={onBack}><ArrowLeft /></TvButton>
        <div><ProviderBadge kind={song.providerKind} /><span><strong>{song.title}</strong><small>{song.artist}</small></span></div>
        <div className="player-header__right">
          <span className={`mic-status mic-status--${microphone.status}`}><Mic2 size={18} />{microphone.status === 'active' ? '麦克风已连接' : '麦克风未开启'}</span>
          <TvButton iconOnly tone="quiet" aria-label="收藏" onClick={() => void setFavorite(song, !song.favorite)}><Heart fill={song.favorite ? 'currentColor' : 'none'} /></TvButton>
          <TvButton tone="quiet" onClick={() => void addToQueue(song)}><ListMusic size={19} />再唱一次</TvButton>
        </div>
      </header>

      <section className="player-main">
        {song.capabilities.video && (
          <div className="player-video-panel">
            {song.media.kind === 'youtube' && song.media.youtubeId ? (
              <YouTubePlayer ref={youtubeRef} videoId={song.media.youtubeId} onTime={onYoutubeTime} onPlaying={onYoutubePlaying} onEnded={onEnded} onError={onYoutubeError} />
            ) : (
              <video
                ref={(element) => { nativeRef.current = element; }}
                src={song.media.url}
                playsInline
                onTimeUpdate={(event) => setCurrentMs(event.currentTarget.currentTime * 1000)}
                onLoadedMetadata={(event) => setDurationMs(event.currentTarget.duration * 1000)}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={onEnded}
              />
            )}
            {error && <div className="player-inline-error">{error}</div>}
          </div>
        )}
        {!song.capabilities.video && (
          <audio
            ref={(element) => { nativeRef.current = element; }}
            src={song.media.url}
            preload="metadata"
            onTimeUpdate={(event) => setCurrentMs(event.currentTarget.currentTime * 1000)}
            onLoadedMetadata={(event) => setDurationMs(event.currentTarget.duration * 1000)}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={onEnded}
          />
        )}

        <div className="lyrics-stage">
          {!song.notes.length ? (
            <div className="lyrics-empty"><Music2Icon /><h2>这首歌暂无逐字歌词</h2><p>你仍然可以播放和录制；连接带有 USDX 或 ASS 的曲库即可显示同步歌词。</p></div>
          ) : (
            <>
              <span className="lyrics-stage__count">{Math.max(1, currentLine + 1)} / {Math.max(...song.notes.map((note) => note.line)) + 1}</span>
              <div className="lyric-line lyric-line--active">
                {activeLine.map((note) => {
                  const progress = currentMs <= note.startMs ? 0 : currentMs >= note.startMs + note.durationMs ? 1 : (currentMs - note.startMs) / note.durationMs;
                  return <span key={note.id} className={progress >= 1 ? 'is-sung' : note.id === currentNote?.id ? 'is-active' : ''} style={{ '--note-progress': `${progress * 100}%` } as React.CSSProperties}>{note.lyric}</span>;
                })}
              </div>
              <div className="lyric-line lyric-line--next">{nextLine.map((note) => note.lyric).join('')}</div>
            </>
          )}
          {song.capabilities.scoring && (
            <div className="pitch-lane">
              <span className="pitch-lane__target" style={{ width: `${Math.max(8, Math.min(42, (currentNote?.durationMs ?? 400) / 50))}%` }} />
              {microphone.midi !== null && currentNote?.pitch !== null && currentNote?.pitch !== undefined && (
                <span className="pitch-lane__voice" style={{ transform: `translateY(${Math.max(-30, Math.min(30, (currentNote.pitch - microphone.midi) * 5))}px)` }} />
              )}
            </div>
          )}
        </div>

        <aside className="score-panel">
          <span>LIVE SCORE</span>
          <strong>{score.toLocaleString()}</strong>
          <div className="score-meter"><i style={{ height: `${microphone.level * 100}%` }} /></div>
          <small>{microphone.status === 'active' ? `${accuracy}% 命中` : '开启麦克风开始评分'}</small>
        </aside>
      </section>

      <footer className="player-hud">
        <div className="progress-row">
          <span>{formatTime(currentMs)}</span>
          <input
            aria-label="播放进度"
            type="range"
            min={0}
            max={Math.max(durationMs, 1)}
            value={Math.min(currentMs, Math.max(durationMs, 1))}
            onChange={(event) => {
              const value = Number(event.target.value);
              setCurrentMs(value);
              if (song.media.kind === 'youtube') youtubeRef.current?.seek(value / 1000);
              else if (nativeRef.current) nativeRef.current.currentTime = value / 1000;
            }}
          />
          <span>-{formatTime(Math.max(0, durationMs - currentMs))}</span>
        </div>
        <div className="player-controls">
          <div>
            <TvButton iconOnly tone="quiet" aria-label={muted ? '恢复声音' : '静音'} onClick={() => {
              setMuted((value) => !value);
              if (nativeRef.current) nativeRef.current.muted = !muted;
              youtubeRef.current?.setVolume(muted ? volume : 0);
            }}>{muted ? <VolumeX /> : <Volume2 />}</TvButton>
            <TvButton tone="quiet" onClick={() => updateVolume(volume - 0.1)} disabled={volume <= 0}>音量 −</TvButton>
            <TvButton tone="quiet" onClick={() => updateVolume(volume + 0.1)} disabled={volume >= 1}>音量 +</TvButton>
          </div>
          <div className="player-controls__center">
            <TvButton iconOnly tone="quiet" aria-label="后退 10 秒" onClick={() => {
              const value = Math.max(0, currentMs - 10_000);
              if (song.media.kind === 'youtube') youtubeRef.current?.seek(value / 1000);
              else if (nativeRef.current) nativeRef.current.currentTime = value / 1000;
            }}><RotateCcw /></TvButton>
            <TvButton iconOnly tone="primary" className="play-toggle" aria-label={playing ? '暂停' : '播放'} onClick={togglePlay}>{playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}</TvButton>
            <TvButton iconOnly tone="quiet" aria-label="前进 10 秒" onClick={() => {
              const value = currentMs + 10_000;
              if (song.media.kind === 'youtube') youtubeRef.current?.seek(value / 1000);
              else if (nativeRef.current) nativeRef.current.currentTime = value / 1000;
            }}><SkipForward /></TvButton>
          </div>
          <div>
            <TvButton tone={microphone.status === 'active' ? 'primary' : 'quiet'} onClick={() => microphone.status === 'active' ? microphone.stop() : void microphone.start()}>
              {microphone.status === 'active' ? <Mic2 /> : <MicOff />}评分
            </TvButton>
            <TvButton tone={microphone.recording ? 'danger' : 'quiet'} onClick={() => void toggleRecording()}>
              {microphone.recording ? <CircleStop fill="currentColor" /> : <Mic2 />}录人声
            </TvButton>
            <TvButton tone="quiet" onClick={cycleSpeed}><Gauge />{speed.toFixed(2)}×</TvButton>
          </div>
        </div>
        {recordingUrl && <a className="recording-download" href={recordingUrl} download={`${song.artist} - ${song.title} - 人声录音.webm`}><Check />下载刚才的录音</a>}
      </footer>

      {finished && (
        <div className="result-overlay">
          <section className="result-card">
            <img src="/brand/pipi-karaoke-mascot.png" alt="" />
            <span className="eyebrow">PERFORMANCE COMPLETE</span>
            <h2>{scoreGrade(score).label}</h2>
            <div className="result-score"><strong style={{ color: scoreGrade(score).color }}>{scoreGrade(score).letter}</strong><span><b>{score.toLocaleString()}</b><small>最终得分</small></span></div>
            <div className="result-stats"><span><strong>{accuracy}%</strong><small>音准命中</small></span><span><strong>{scoreSamples.perfect}</strong><small>完美音符</small></span><span><strong>{formatTime(durationMs)}</strong><small>演唱时长</small></span></div>
            <div className="result-actions">
              <TvButton onClick={retry}><RotateCcw />再唱一次</TvButton>
              {nextQueueItem ? <TvButton tone="primary" onClick={() => navigate(`/play/${encodeURIComponent(nextQueueItem.song.id)}`, { state: { queueId: nextQueueItem.id } })}>下一首 <ChevronRight /></TvButton> : <TvButton tone="primary" onClick={() => navigate('/')}><Sparkles />回到首页</TvButton>}
            </div>
          </section>
        </div>
      )}
    </main>
  );
}

function Music2Icon() {
  return <span className="music-placeholder">♪</span>;
}
